import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { Panel } from '../../components/primitives/Panel';
import { SMLButton } from '../../components/primitives/SMLButton';
import { aiNudgeMessages as messages } from './aiNudgeMessages';
import { useAiNudge } from './useAiNudge';
import styles from './HomeAiNudge.module.scss';
import { withBrandWord } from '../../components/primitives/BrandWord';

/**
 * The AI assistants card at the top of the artist Home (design: ai-assistants n1-n5). It renders
 * nothing until `oauthClients.list` answered, so it shows no skeleton. "Set it up" opens
 * Settings → AI assistants and keeps the card: the card goes when an assistant connects or when
 * the user closes it.
 */
const HomeAiNudgeComponent: React.FC = () => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { isVisible, dismiss } = useAiNudge();
  const setUp = useCallback(() => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.aiAssistants }), [router]);

  if (!isVisible) {
    return null;
  }

  const title = intl.formatMessage(messages.title);
  return (
    <Panel as="section" className={styles.card} aria-label={title} data-testid="home-ai-nudge">
      <span className={styles.icon} aria-hidden="true">
        <FontAwesomeIcon icon={faSparkles} />
      </span>
      <div className={styles.text}>
        <b>{title}</b>
        <p>{withBrandWord(intl.formatMessage(messages.body))}</p>
      </div>
      <SMLButton variant="primary" className={styles.cta} onClick={setUp}>
        {intl.formatMessage(messages.cta)}
      </SMLButton>
      <button
        type="button"
        className={styles.close}
        onClick={dismiss}
        title={intl.formatMessage(messages.dismiss)}
        aria-label={intl.formatMessage(messages.dismiss)}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </Panel>
  );
};

export const HomeAiNudge = TypedMemo(HomeAiNudgeComponent);
